import { FC, useCallback, useEffect, useState } from "react";
import { Ad, EditAd, Picture } from "../../../types";
import { Button } from "../../UI/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { Col, Container, Row } from "../../common/Grid";
import { Input, Label, Radio, Textarea } from "../../UI/Forms/FormElements";
import { FormControl } from "@mui/material";
import Feedback from "../../UI/Forms/FormElements/Feedback";
import { DatePicker } from "../../common/DatePicker";
import { toHHMMSS, toSeconds } from "../../../services/utils";
import { useDisclosure } from "react-use-disclosure";
import { FileUploadModal } from "../../common/FileUploadModal";
import { observer } from "mobx-react";

type Props = {
  ad?: Ad;
  isSubmitting: boolean;
  onSubmit: (values: EditAd) => void;
  editable?: boolean;
  isAdmin?: boolean;
  validable: boolean;
  onCancel: () => void;
};

const AdForm: FC<Props> = observer(
  ({
    ad,
    isSubmitting,
    onSubmit,
    editable = true,
    isAdmin = false,
    validable,
    onCancel,
  }) => {
    const { t } = useTranslation();
    const {
      isOpen: isModalUploadOpen,
      open: openModalUpload,
      close: closeModalUpload,
    } = useDisclosure();
    const [coverImage, setCoverImage] = useState<any>();
    const [mediaDuration, setMediaDuration] = useState(
      ad?.duration ? toHHMMSS(ad.duration) : "00:00:00"
    );

    const formik = useFormik({
      initialValues: {
        id: ad?.id || null,
        title: ad?.title || "",
        description: ad?.description || "",
        pictures: ad?.pictures || ({} as Picture),
        externalId: ad?.externalId || "",
        onClickUrl: ad?.onClickUrl || "",
        duration: ad?.duration || undefined,
        validFrom: ad?.validFrom ? new Date(ad?.validFrom) : undefined,
        validUntil: ad?.validUntil ? new Date(ad?.validUntil) : undefined,
        canBeSkipped:
          ad?.canBeSkipped === true ? "canBeSkipped" : "canNotBeSkipped",
        mediaType: ad?.mediaType || "youtube",
        mediaUrl: ad?.mediaUrl || "",
      },
      enableReinitialize: true,
      onSubmit: onSubmit,
      validationSchema: Yup.object().shape({
        title: Yup.string().required(
          t("components.screenAdvertisingDetail.adForm.titleRequired")
        ),
        mediaUrl: Yup.string().required(
          t("components.screenAdvertisingDetail.adForm.urlRequired")
        ),
        onClickUrl: Yup.string().required(
          t("components.screenAdvertisingDetail.adForm.onClickUrlRequired")
        ),
        validFrom: Yup.string().required(
          t("components.screenAdvertisingDetail.adForm.validFromRequired")
        ),
        validUntil: Yup.string().required(
          t("components.screenAdvertisingDetail.adForm.validUntilRequired")
        ),
        canBeSkipped: Yup.string().required(
          t("components.screenAdvertisingDetail.adForm.validUntilRequired")
        ),
      }),
    });

    // Generate YouTube thumbnail when externalId changes
    useEffect(() => {
      if (formik.values.externalId && formik.values.mediaType === "youtube") {
        const videoId = formik.values.externalId;
        const maxResUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
        const hqUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

        const img = new Image();
        img.onload = () => {
          const bestUrl = img.naturalWidth > 120 ? maxResUrl : hqUrl;
          setCoverImage(bestUrl);

          formik.setFieldValue("pictures", {
            link: bestUrl,
            link_with_play_button: "",
            sizes: [
              { height: 1080, width: 1920, link: bestUrl },
              { height: 720, width: 1280, link: bestUrl },
              { height: 540, width: 960, link: bestUrl },
              {
                height: 360,
                width: 640,
                link: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
              },
              {
                height: 166,
                width: 295,
                link: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
              },
              {
                height: 150,
                width: 200,
                link: `https://img.youtube.com/vi/${videoId}/sddefault.jpg`,
              },
              {
                height: 75,
                width: 100,
                link: `https://img.youtube.com/vi/${videoId}/sddefault.jpg`,
              },
            ]
              .reverse()
              .map((i) => ({ link: i.link, width: i.width, height: i.height })),
          } as any);
        };
        img.onerror = () => {
          setCoverImage(hqUrl);
          formik.setFieldValue("pictures", {
            link: hqUrl,
            link_with_play_button: "",
            sizes: [
              { height: 1080, width: 1920, link: hqUrl },
              { height: 720, width: 1280, link: hqUrl },
              { height: 540, width: 960, link: hqUrl },
              { height: 360, width: 640, link: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` },
              { height: 166, width: 295, link: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` },
              { height: 150, width: 200, link: `https://img.youtube.com/vi/${videoId}/sddefault.jpg` },
              { height: 75, width: 100, link: `https://img.youtube.com/vi/${videoId}/sddefault.jpg` },
            ]
              .reverse()
              .map((i) => ({ link: i.link, width: i.width, height: i.height })),
          } as any);
        };
        img.src = maxResUrl;
      } else if (ad) {
        try {
          setCoverImage(
            ad.pictures
              ? ad.pictures.sizes
                ? ad.pictures.sizes[6].link
                : ad.pictures[6].link
              : ""
          );
        } catch (_) {}
      }
    }, [formik.values.externalId, formik.values.mediaType]);

    const handleUploadPoster = useCallback(
      async (imageUrls: string[]) => {
        formik.setFieldValue("posterUrl", imageUrls[0]);
        closeModalUpload();
      },
      [formik, closeModalUpload]
    );

    return (
      <form onSubmit={formik.handleSubmit}>
        <Container pb={"20px"}>
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="mediaUrl">
                  {t("components.screenAdvertisingDetail.adForm.url")}
                </Label>
                <Input
                  id="mediaUrl"
                  name="mediaUrl"
                  placeholder={"https://youtu.be/..."}
                  value={formik.values.mediaUrl}
                  onChange={(e) => {
                    formik.handleChange(e);
                    formik.setFieldValue(
                      "externalId",
                      Boolean(e.target?.value)
                        ? e.target.value.split("/").pop()
                        : null
                    );
                  }}
                  state={formik.errors.mediaUrl ? "error" : "success"}
                  showState={true}
                  feedbackText={formik.getFieldMeta("mediaUrl").error}
                />
                {formik.errors.externalId && (
                  <Feedback state="error" showState={true} showErrorOnly={true}>
                    {formik.getFieldMeta("externalId").error}
                  </Feedback>
                )}
              </FormControl>
            </Col>
          </Row>
          <Row mt="10px">
            {coverImage && (
              <Label htmlFor="title">
                {t("components.screenProgramDetail.videoForm.coverImage")}
              </Label>
            )}
            <img src={coverImage} alt={formik.values.title} />
          </Row>
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="title">
                  {t("components.screenAdvertisingDetail.adForm.title")}
                </Label>
                <Input
                  id="title"
                  name="title"
                  placeholder={t(
                    "components.screenAdvertisingDetail.adForm.titlePlaceholder"
                  )}
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  state={formik.errors.title ? "error" : "success"}
                  showState={true}
                  feedbackText={formik.getFieldMeta("title").error}
                />
              </FormControl>
            </Col>
          </Row>
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="description">
                  {t("components.screenAdvertisingDetail.adForm.description")}
                </Label>
                <Textarea
                  id="description"
                  name="description"
                  placeholder={t(
                    "components.screenAdvertisingDetail.adForm.descriptionPlaceholder"
                  )}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  showState={true}
                />
              </FormControl>
            </Col>
          </Row>

          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="duration">
                  {t("components.screenAdvertisingDetail.adForm.duration")}
                </Label>
                <Input
                  id="duration"
                  name="duration"
                  placeholder={t(
                    "components.screenAdvertisingDetail.adForm.durationPlaceholder"
                  )}
                  value={mediaDuration}
                  onChange={(e) => {
                    setMediaDuration(e.target.value);
                    if (String(e.target?.value).split(":").length === 3) {
                      formik.setFieldValue(
                        "duration",
                        toSeconds(e.target.value)
                      );
                    }
                  }}
                  state={formik.errors.duration ? "error" : "success"}
                  showState={true}
                  feedbackText={formik.getFieldMeta("duration").error}
                />
              </FormControl>
            </Col>
          </Row>

          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="onClickUrl">
                  {t("components.screenAdvertisingDetail.adForm.onClickUrl")}
                </Label>
                <Input
                  id="onClickUrl"
                  name="onClickUrl"
                  placeholder={t(
                    "components.screenAdvertisingDetail.adForm.onClickUrlPlaceholder"
                  )}
                  value={formik.values.onClickUrl}
                  onChange={formik.handleChange}
                  state={formik.errors.onClickUrl ? "error" : "success"}
                  showState={true}
                  feedbackText={formik.getFieldMeta("onClickUrl").error}
                />
              </FormControl>
            </Col>
          </Row>

          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="validFrom">
                  {t("components.screenAdvertisingDetail.adForm.validFrom")}
                </Label>
                <DatePicker
                  // @ts-ignore
                  size="sm"
                  placeholderText={t(
                    "components.screenAdvertisingDetail.adForm.validFromPlaceholder"
                  )}
                  dateFormat={"dd/MM/yyyy"}
                  selectedDate={formik.values.validFrom || undefined}
                  onChange={(date) => {
                    formik.setFieldValue("validFrom", date || null);
                  }}
                />
                {formik.errors.validFrom && (
                  <Feedback state="error" showState={true} showErrorOnly={true}>
                    {formik.getFieldMeta("validFrom").error}
                  </Feedback>
                )}
              </FormControl>
            </Col>
          </Row>
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="validUntil">
                  {t("components.screenAdvertisingDetail.adForm.validUntil")}
                </Label>

                <DatePicker
                  // @ts-ignore
                  size="sm"
                  placeholderText={t(
                    "components.screenAdvertisingDetail.adForm.validUntilPlaceholder"
                  )}
                  dateFormat={"dd/MM/yyyy"}
                  selectedDate={formik.values.validUntil || undefined}
                  onChange={(date) => {
                    formik.setFieldValue("validUntil", date || null);
                  }}
                />
                {formik.errors.validUntil && (
                  <Feedback state="error" showState={true} showErrorOnly={true}>
                    {formik.getFieldMeta("validUntil").error}
                  </Feedback>
                )}
              </FormControl>
            </Col>
          </Row>

          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="highlighted">
                  {t("components.screenAdvertisingDetail.adForm.canBeSkipped")}
                </Label>
                <Radio
                  id="canBeSkipped1"
                  name="canBeSkipped"
                  value={"canBeSkipped"}
                  label={t(
                    "components.screenAdvertisingDetail.adForm.isCanBeSkipped"
                  )}
                  onChange={formik.handleChange}
                  checked={formik.values.canBeSkipped === "canBeSkipped"}
                />
                <Radio
                  id="canBeSkipped2"
                  name="canBeSkipped"
                  value={"canNotBeSkipped"}
                  label={t(
                    "components.screenAdvertisingDetail.adForm.isNotCanBeSkipped"
                  )}
                  onChange={formik.handleChange}
                  checked={formik.values.canBeSkipped === "canNotBeSkipped"}
                />

                {formik.errors.canBeSkipped && (
                  <Feedback state="error" showState={true} showErrorOnly={true}>
                    {formik.getFieldMeta("canBeSkipped").error}
                  </Feedback>
                )}
              </FormControl>
            </Col>
          </Row>
        </Container>

        <Row>
          <Col textAlign="end">
            <Button
              type={"reset"}
              onClick={onCancel}
              marginRight="10px"
              color="light"
            >
              {t("common.cancel")}
            </Button>
            <Button type={"submit"}>{t("common.save")}</Button>
          </Col>
        </Row>

        <FileUploadModal
          isOpen={isModalUploadOpen}
          onClose={closeModalUpload}
          onFileUploaded={handleUploadPoster}
          maxFiles={1}
          accept="image/jpeg, image/png"
        />
      </form>
    );
  }
);

export default AdForm;
